import { getGetMeQueryOptions, isApiError } from "@scalephant/contract/client";
import type { MeResponse } from "@scalephant/contract";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { authClient } from "../../lib/auth-client";

/**
 * The signed-in state comes from the api's /me (user + session + memberships), not from
 * Better Auth's client, so the app sees exactly what the api enforces (session policy,
 * active organization). 401 means signed out; everything else is a real error.
 */

export function meQueryOptions() {
  return {
    ...getGetMeQueryOptions(),
    retry: false,
    staleTime: 30_000,
  };
}

export function useMe() {
  return useQuery(meQueryOptions());
}

export async function loadMe(queryClient: QueryClient): Promise<MeResponse | null> {
  try {
    return await queryClient.query(meQueryOptions());
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

/** For `beforeLoad` of authenticated routes: throws a redirect to /login when signed out. */
export async function requireAuth(queryClient: QueryClient, href: string): Promise<MeResponse> {
  const me = await loadMe(queryClient);
  if (!me) {
    throw redirect({ to: "/login", search: { redirect: href } });
  }
  return me;
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return async () => {
    await authClient.signOut();
    queryClient.removeQueries({ queryKey: meQueryOptions().queryKey });
    await queryClient.invalidateQueries();
  };
}

export function useInvalidateMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: meQueryOptions().queryKey });
}
